// Library Methods
import { useEffect, useState } from "react";
import { arrayMove } from "@dnd-kit/sortable";

// Components
import Grid from "@mui/material/Grid";
import SelectionScreen from "./screens/SelectionScreen/SelectionScreen";
import ComparisonScreen from "./screens/ComparisonScreen/ComparisonScreen";
import ExamDetailsScreen from "./screens/ExamDetailsScreen/ExamDetailsScreen";
import {
  GridLayoutValue,
  getDefaultGridLayout,
} from "./screens/ComparisonScreen/GridLayoutPopover/GridLayoutPopover";

const ExamInsightsModal = () => {
  const [selectedExamIds, setSelectedExamIds] = useState<Set<string>>(
    new Set()
  );

  const toggleExamSelection = (examId: string) => {
    setSelectedExamIds((prev) => {
      const next = new Set(prev);
      if (next.has(examId)) {
        next.delete(examId);
      } else {
        next.add(examId);
      }
      return next;
    });
  };

  // Selects/deselects every exam in a date group at once, via its accordion checkbox
  const toggleDateSelection = (
    examIds: Array<string>,
    shouldSelectAll: boolean
  ) => {
    setSelectedExamIds((prev) => {
      const next = new Set(prev);
      examIds.forEach((examId) => {
        if (shouldSelectAll) {
          next.add(examId);
        } else {
          next.delete(examId);
        }
      });
      return next;
    });
  };

  // Lives here (not inside ComparisonScreen) so the drag order survives
  // switching away to the Selection screen and back -- ComparisonScreen
  // unmounts on every screen switch, which would otherwise reset it.
  const [orderedExamIds, setOrderedExamIds] = useState<Array<string>>([]);

  useEffect(() => {
    setOrderedExamIds((prev) => {
      const stillSelected = prev.filter((id) => selectedExamIds.has(id));
      const newIds = [...selectedExamIds].filter((id) => !prev.includes(id));
      return [...stillSelected, ...newIds];
    });
  }, [selectedExamIds]);

  const reorderExamIds = (activeId: string, overId: string) => {
    setOrderedExamIds((prev) => {
      const oldIndex = prev.indexOf(activeId);
      const newIndex = prev.indexOf(overId);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  // Lives here for the same reason as orderedExamIds above -- survives
  // ComparisonScreen unmounting when the user goes to view an exam's
  // details and back, resetting only when they return to the Selection
  // screen (which discards selectedExamIds/orderedExamIds too).
  const [gridLayout, setGridLayout] = useState<GridLayoutValue>(() =>
    getDefaultGridLayout(selectedExamIds.size)
  );

  // Screen #1 (default): the selection/sorting view below.
  // Screen #2: comparison mode, reached by double-clicking a selected card.
  // Screen #3: a single exam's full image, reached by clicking (not dragging)
  // a card on the comparison screen.
  const [activeScreen, setActiveScreen] = useState<
    "selection" | "comparison" | "examDetails"
  >("selection");
  const [detailExamId, setDetailExamId] = useState<string | null>(null);

  // Each visit to the comparison screen from the Selection screen starts from the layout
  // that fits the exams picked this time
  const handleEnterComparisonScreen = () => {
    setGridLayout(getDefaultGridLayout(selectedExamIds.size));
    setActiveScreen("comparison");
  };

  const handleViewExamDetails = (examId: string) => {
    setDetailExamId(examId);
    setActiveScreen("examDetails");
  };

  return (
    <Grid
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        width: "100%",
      }}
    >
      {activeScreen === "examDetails" && detailExamId ? (
        <ExamDetailsScreen
          examId={detailExamId}
          onBack={() => setActiveScreen("comparison")}
        />
      ) : activeScreen === "comparison" ? (
        <ComparisonScreen
          selectedExamIds={selectedExamIds}
          orderedExamIds={orderedExamIds}
          onReorderExamIds={reorderExamIds}
          onToggleExamSelection={toggleExamSelection}
          onViewExamDetails={handleViewExamDetails}
          gridLayout={gridLayout}
          onGridLayoutChange={setGridLayout}
          onBack={() => setActiveScreen("selection")}
        />
      ) : (
        <SelectionScreen
          selectedExamIds={selectedExamIds}
          onToggleExamSelection={toggleExamSelection}
          onToggleDateSelection={toggleDateSelection}
          onEnterComparisonScreen={handleEnterComparisonScreen}
        />
      )}
    </Grid>
  );
};

export default ExamInsightsModal;
